import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Building2,
  Mail,
  CarFront,
  Phone,
  BadgeCheck,
  ArrowRight,
  ChevronDown,
  Globe2,
  Loader2,
  CheckCircle2,
  Headphones,
} from 'lucide-react';

import logo from '../logo.png';
import ReservationFlow from '../components/reservation/ReservationFlow';
import './LandingPage.css';

async function smartAuthMode() {
  const session = await fetchAuthSession();
  if (session?.tokens?.accessToken) return 'userPool';
  if (session?.credentials) return 'iam';
  return 'iam';
}

const client = createAuditedClient('LandingPage');

function safeStr(value) {
  return (value || '').toString().trim();
}

const LIST_COMPANIES = /* GraphQL */ `
  query ListCompanies($limit: Int, $nextToken: String) {
    listCompanies(limit: $limit, nextToken: $nextToken) {
      items { id name country currencies pickupLocations dropoffLocations }
      nextToken
    }
  }
`;

const LIST_CARS = /* GraphQL */ `
  query ListCars($limit: Int, $nextToken: String) {
    listCars(limit: $limit, nextToken: $nextToken) {
      items {
        id
        CompanyId
        name
        plate
        active
        transmission
        fuelType
        year
        category
        bodyType
        imageKey
        startingPrice
        seats
      }
      nextToken
    }
  }
`;

const SUBMIT_INBOUND = /* GraphQL */ `
  mutation SubmitInboundRequest($input: SubmitInboundRequestInput!) {
    submitInboundRequest(input: $input) {
      id
      createdAt
    }
  }
`;

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const WHATSAPP_NUMBER = '+995595004656';
const WHATSAPP_E164 = WHATSAPP_NUMBER.replace(/[^\d]/g, '');
const CONTACT_EMAIL = 'info@hastecarrental.com';
const PUBLIC_RESERVATION_COUNTRIES = ['Georgia'];

function getScrollBehavior() {
  if (typeof window === 'undefined') return 'auto';
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
}

function normalizeCompanyCountry(value) {
  const country = safeStr(value).toLowerCase();
  if (country === 'ge' || country === 'geo' || country === 'georgia') return 'georgia';
  if (country === 'al' || country === 'alb' || country === 'albania') return 'albania';
  return country;
}

function isPublicReservationCountry(value) {
  return normalizeCompanyCountry(value) === 'georgia';
}

function Notice({ tone = 'success', title, children, meta, onClose, compact = false }) {
  const tones = {
    success: {
      icon: <CheckCircle2 size={16} />,
      className: 'is-success',
    },
    error: {
      icon: <BadgeCheck size={16} />,
      className: 'is-error',
    },
    info: {
      icon: <Mail size={16} />,
      className: 'is-info',
    },
    warn: {
      icon: <Phone size={16} />,
      className: 'is-warn',
    },
  };

  const toneMeta = tones[tone] || tones.success;

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className={`lp-notice ${toneMeta.className} ${compact ? 'is-compact' : ''}`}
    >
      <div className="lp-notice-icon">{toneMeta.icon}</div>
      <div className="lp-notice-content">
        <div className="lp-notice-title">{title}</div>
        <div className="lp-notice-text">{children}</div>

        {(meta || onClose) && (
          <div className="lp-notice-meta-row">
            {meta ? <span className="lp-notice-meta">{meta}</span> : null}
            {onClose ? (
              <button type="button" className="lp-note-close" onClick={onClose}>
                Dismiss
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function HeroSection({
  dataLoading,
  dataErr,
  onReserveClick,
  onContactClick,
}) {
  const heroImage = '/assets/landing-hero-car-1600.jpg';
  const heroImageSmall = '/assets/landing-hero-car-960.jpg';
  const inventoryLabel = dataLoading
    ? 'Checking availability'
    : dataErr
      ? 'Booking team online'
      : 'Live availability';
  const essentials = [
    { icon: <CarFront size={18} />, label: 'Real availability' },
    { icon: <Globe2 size={18} />, label: 'Unlimited mileage' },
    { icon: <Headphones size={18} />, label: 'Roadside support' },
  ];

  return (
    <section className="lp-hero" aria-labelledby="lp-hero-title">
      <div className="lp-hero-grid">
        <div className="lp-hero-copy" data-reveal="left">
          <div className="lp-eyebrow">
            <span>Haste</span>
            <span className="lp-eyebrow-line" />
            <span>Car rental</span>
          </div>

          <h1 id="lp-hero-title" className="lp-hero-title">
            The road,<br />
            <em>on your terms.</em>
          </h1>
          <p className="lp-hero-tagline">Real cars. Clear rates. Local support.</p>

          <div className="lp-hero-actions">
            <button type="button" className="lp-btn lp-btn--primary" onClick={onReserveClick}>
              Find a car <ArrowRight size={17} />
            </button>
            <button type="button" className="lp-text-link" onClick={onContactClick}>
              Talk to us <ArrowRight size={15} />
            </button>
          </div>

          <div className="lp-live-line">
            <span className="lp-live-dot" />
            {inventoryLabel}
          </div>
        </div>

        <div className="lp-hero-visual" data-reveal="right">
        <picture className="lp-hero-picture">
          <source media="(max-width: 720px)" srcSet={heroImageSmall} />
          <source srcSet={`${heroImageSmall} 960w, ${heroImage} 1600w`} sizes="(max-width: 900px) 100vw, 58vw" />
          <img
            src={heroImage}
            srcSet={`${heroImageSmall} 960w, ${heroImage} 1600w`}
            sizes="(max-width: 900px) 100vw, 58vw"
            width="1600"
            height="800"
            alt="A Haste rental car ready for the road"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            referrerPolicy="no-referrer"
            className="lp-hero-image"
            onError={event => {
              event.currentTarget.style.display = 'none';
            }}
          />
        </picture>
          <div className="lp-hero-scrim" />
          <div className="lp-offer-card">
            <strong>10% off</strong>
            <span>when you book online</span>
          </div>
          <div className="lp-location-stamp">
            <Globe2 size={15} /> Wherever next
          </div>
        </div>
      </div>

      <div className="lp-trust-row" aria-label="Rental essentials" data-reveal="up">
        {essentials.map((item, index) => (
          <div key={item.label} className="lp-trust-item" style={{ '--reveal-delay': `${index * 70}ms` }}>
            {item.icon}
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQSection({ onContactClick, onPartnerClick }) {
  const items = [
    {
      id: 'instant-booking',
      question: 'Can I book instantly online?',
      answer:
        'You can submit your reservation request online at any time, but it is not instantly confirmed. The booking becomes confirmed only after the team approves it or payment is confirmed when required.',
    },
    {
      id: 'documents',
      question: 'What documents do I need?',
      answer:
        'Bring a valid physical passport or national ID and your original physical driving license. The main driver must present physical documents at handover; digital copies are not accepted.',
    },
    {
      id: 'flight-delay',
      question: 'What happens if my flight is delayed?',
      answer:
        'Send us your flight number when booking and contact the team as soon as possible if your arrival changes. We will do our best to adjust the pickup time based on your updated arrival and availability.',
    },
    {
      id: 'cancellation',
      question: 'What is your cancellation policy?',
      answer:
        'Cancellation requests made less than 72 hours before the reservation start date are non-refundable.',
    },
    {
      id: 'cross-border',
      question: 'What is your cross-border policy?',
      answer:
        'Cross-border driving is not available for Georgia reservations. Vehicles rented in Georgia may not cross international borders.',
    },
    {
      id: 'deposit',
      question: 'What is your deposit policy?',
      answer:
        'A security deposit may be required depending on the vehicle category and country. Deposit terms are shown during the reservation flow, and in many cases the deposit is held on a card until the vehicle is returned and checked.',
    },
    {
      id: 'mileage',
      question: 'Are there mileage limits?',
      answer: 'No. Our standard rentals include unlimited mileage.',
    },
    {
      id: 'minimum-age',
      question: 'What is the minimum age to rent?',
      answer:
        'The minimum rental age is 21. The main driver must also hold a valid driving license that meets the rental requirements.',
    },
    {
      id: 'insurance',
      question: 'What insurance policies are available?',
      answer:
        'Standard rentals include third-party liability insurance. Full coverage can be added where available, but coverage depends on following the rental terms and reporting any accident or damage immediately.',
    },
    {
      id: 'roadside',
      question: 'Do you provide roadside assistance?',
      answer:
        'Yes. If you have a breakdown, accident, or technical issue during the rental, contact the team immediately so we can assist and guide the next step.',
    },
  ];
  const [openItem, setOpenItem] = useState('instant-booking');

  return (
    <>
      <section className="lp-how" aria-labelledby="lp-how-title" data-reveal="up">
        <div className="lp-section-kicker">The simple route</div>
        <div className="lp-how-grid">
          <div className="lp-how-heading">
            <h2 id="lp-how-title">Pick up.<br />Drive out.</h2>
          </div>
          {[
            ['01', 'Choose', 'Dates, place, car.'],
            ['02', 'Tailor', 'Cover and extras.'],
            ['03', 'Go', 'We handle the rest.'],
          ].map(([number, title, text], index) => (
            <article key={number} className="lp-how-step" style={{ '--reveal-delay': `${index * 90}ms` }}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lp-partner-strip" data-reveal="up">
        <div>
          <span>For rental operators</span>
          <h2>Grow with Haste.</h2>
        </div>
        <button type="button" className="lp-btn lp-btn--lime" onClick={onPartnerClick}>
          Partner with us <ArrowRight size={16} />
        </button>
      </section>

      <section className="lp-faq-section" aria-labelledby="lp-faq-title">
        <div className="lp-faq-intro" data-reveal="left">
          <div className="lp-section-kicker">Good to know</div>
          <h2 id="lp-faq-title">Before you go.</h2>
          <button type="button" className="lp-text-link" onClick={onContactClick}>
            Ask a question <ArrowRight size={15} />
          </button>
        </div>

        <div className="lp-faq-list" data-reveal="right">
          {items.map((item, index) => {
            const panelId = `faq-panel-${item.id}`;
            const isOpen = openItem === item.id;
            return (
              <div
                key={item.id}
                className={`lp-faq-item ${isOpen ? 'is-open' : ''}`}
                style={{ '--reveal-delay': `${index * 45}ms` }}
              >
                <button
                  type="button"
                  className="lp-faq-trigger"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenItem(isOpen ? '' : item.id)}
                >
                  <span className="lp-faq-number">{String(index + 1).padStart(2, '0')}</span>
                  <span>{item.question}</span>
                  <ChevronDown size={18} className="lp-faq-chevron" />
                </button>
                <div id={panelId} className="lp-faq-answer" aria-hidden={!isOpen}>
                  <div className="lp-faq-answer-inner">
                    <p>{item.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <aside className="lp-contact-mini" data-reveal="up">
          <div>
            <Headphones size={19} />
            <span>Need help now?</span>
          </div>
          <a href={`https://wa.me/${WHATSAPP_E164}`} target="_blank" rel="noreferrer">
            WhatsApp <ArrowRight size={15} />
          </a>
        </aside>
      </section>
    </>
  );
}

function useTurnstileScript() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (window.turnstile) {
      setReady(true);
      return;
    }

    const existing = document.querySelector('script[data-turnstile="1"]');
    if (existing) {
      const onLoad = () => setReady(true);
      existing.addEventListener('load', onLoad);
      return () => existing.removeEventListener('load', onLoad);
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.turnstile = '1';
    script.onload = () => setReady(true);
    script.onerror = () => setReady(false);
    document.body.appendChild(script);
  }, []);

  return ready;
}

function TurnstileWidget({ siteKey, onToken, onExpire, onError, action = 'landingpage', theme = 'light' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const ready = useTurnstileScript();

  useEffect(() => {
    if (!ready || !siteKey || !containerRef.current || !window.turnstile) return;

    try {
      containerRef.current.innerHTML = '';
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        action,
        callback: token => onToken?.(token),
        'expired-callback': () => onExpire?.(),
        'error-callback': () => onError?.(),
      });
    } catch {
      onError?.();
    }

    return () => {
      try {
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch {
        // ignore turnstile teardown errors
      }
      widgetIdRef.current = null;
    };
  }, [ready, siteKey, action, theme, onToken, onExpire, onError]);

  if (!siteKey) {
    return (
      <div className="lp-inline-alert">
        Missing Turnstile site key. Set <b>REACT_APP_TURNSTILE_SITE_KEY</b>.
      </div>
    );
  }

  return <div ref={containerRef} />;
}

export default function LandingPage() {
  const year = new Date().getFullYear();

  const location = useLocation();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [cars, setCars] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataErr, setDataErr] = useState(null);

  const [agencyWebsite, setAgencyWebsite] = useState('');
  const [contactWebsite, setContactWebsite] = useState('');

  const [active, setActive] = useState(() => hashToSection(location.hash) || 'reserve');
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [compactHeader, setCompactHeader] = useState(false);

  const reservePanelRef = useRef(null);
  const partnerPanelRef = useRef(null);
  const partnerFormRef = useRef(null);
  const contactPanelRef = useRef(null);

  const [agencyName, setAgencyName] = useState('');
  const [agencyCountry, setAgencyCountry] = useState('');
  const [agencyEmail, setAgencyEmail] = useState('');
  const [agencyPhone, setAgencyPhone] = useState('');
  const [agencyFleetSize, setAgencyFleetSize] = useState('');
  const [agencyLocationsCount, setAgencyLocationsCount] = useState('');
  const [agencyMessage, setAgencyMessage] = useState('');
  const [submittingPartner, setSubmittingPartner] = useState(false);
  const [partnerDone, setPartnerDone] = useState(null);
  const [partnerSubmitLocked, setPartnerSubmitLocked] = useState(false);
  const [partnerCaptchaToken, setPartnerCaptchaToken] = useState('');

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMsg, setContactMsg] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactReason, setContactReason] = useState('GENERAL');
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactDone, setContactDone] = useState(null);
  const [contactSubmitLocked, setContactSubmitLocked] = useState(false);
  const [contactCaptchaToken, setContactCaptchaToken] = useState('');

  const reservationCompanies = useMemo(
    () => (companies || []).filter(company => isPublicReservationCountry(company?.country)),
    [companies]
  );

  const reservationCompanyIds = useMemo(
    () => new Set(reservationCompanies.map(company => company?.id).filter(Boolean)),
    [reservationCompanies]
  );

  const reservationCars = useMemo(
    () => (cars || []).filter(car => car?.active && reservationCompanyIds.has(car?.CompanyId)),
    [cars, reservationCompanyIds]
  );

  const reservationCountryOptions = useMemo(() => {
    const configuredCountries = new Set(
      reservationCompanies.map(company => normalizeCompanyCountry(company?.country)).filter(Boolean)
    );
    const configuredOptions = PUBLIC_RESERVATION_COUNTRIES.filter(countryName =>
      configuredCountries.has(normalizeCompanyCountry(countryName))
    );
    return configuredOptions.length ? configuredOptions : PUBLIC_RESERVATION_COUNTRIES;
  }, [reservationCompanies]);

  function hashToSection(hash) {
    const normalizedHash = safeStr(hash).replace(/^#/, '').toLowerCase();
    if (!normalizedHash) return null;
    if (normalizedHash === 'reserve' || normalizedHash === 'book' || normalizedHash === 'booking') return 'reserve';
    if (normalizedHash === 'franchise' || normalizedHash === 'partner' || normalizedHash === 'agency') return 'partner';
    if (normalizedHash === 'contact' || normalizedHash === 'support') return 'contact';
    return null;
  }

  function sectionToHash(section) {
    if (section === 'reserve') return '#reserve';
    if (section === 'partner') return '#franchise';
    if (section === 'contact') return '#contact';
    return '';
  }

  function scrollToSection(section) {
    const scheduleScroll = callback => {
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(callback);
        return;
      }
      setTimeout(callback, 0);
    };

    scheduleScroll(() => {
      const target =
        section === 'partner'
          ? partnerPanelRef.current
          : {
            reserve: reservePanelRef,
            contact: contactPanelRef,
          }[section]?.current;

      target?.scrollIntoView?.({ behavior: getScrollBehavior(), block: 'start' });
    });
  }

  function goTo(section, options = { updateHash: true }) {
    setActive(section);

    if (options?.updateHash) {
      navigate({ pathname: location.pathname, hash: sectionToHash(section) }, { replace: true });
    }

    if (section === active) scrollToSection(section);
  }

  useEffect(() => {
    const section = hashToSection(location.hash);
    if (!section || section === active) return;
    setActive(section);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  useEffect(() => {
    const hashSection = hashToSection(location.hash);
    if (hashSection && hashSection !== active) return;

    // Keep a plain visit to "/" at the top of the landing page.
    if (!location.hash && active === 'reserve') return;
    if (location.hash && !hashSection) return;

    const expectedHash = sectionToHash(active);
    if (expectedHash && location.hash !== expectedHash) {
      navigate({ pathname: location.pathname, hash: expectedHash }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, location.hash]);

  useEffect(() => {
    const section = hashToSection(location.hash);
    if (!section || section !== active) return;
    scrollToSection(section);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, location.hash]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const root = document.querySelector('.lp-root');
    if (!root) return undefined;

    const revealItems = Array.from(root.querySelectorAll('[data-reveal]:not(.is-visible)'));
    if (!revealItems.length) return undefined;

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach(item => item.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    const revealFocusedContent = event => {
      event.target.closest?.('[data-reveal]')?.classList.add('is-visible');
    };

    root.addEventListener('focusin', revealFocusedContent);
    revealItems.forEach(item => observer.observe(item));
    return () => {
      observer.disconnect();
      root.removeEventListener('focusin', revealFocusedContent);
    };
  }, [active]);

  useEffect(() => {
    if (!isMobile) {
      setCompactHeader(false);
      return;
    }

    let frame = 0;
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setCompactHeader((window.scrollY || 0) > 12);
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isMobile]);

  useEffect(() => {
    if (partnerDone?.ok) {
      const timeoutId = setTimeout(() => setPartnerDone(null), 8000);
      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [partnerDone]);

  useEffect(() => {
    if (contactDone?.ok) {
      const timeoutId = setTimeout(() => setContactDone(null), 8000);
      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [contactDone]);

  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      setDataLoading(true);
      setDataErr(null);

      try {
        let allCompanies = [];
        let nextToken = null;

        do {
          const companyResult = await client.graphql({
            query: LIST_COMPANIES,
            variables: { limit: 200, nextToken },
            authMode: await smartAuthMode(),
          });

          const payload = companyResult?.data?.listCompanies;
          nextToken = payload?.nextToken || null;
          allCompanies = allCompanies.concat((payload?.items || []).filter(Boolean));
        } while (nextToken);

        let allCars = [];
        nextToken = null;

        do {
          const carResult = await client.graphql({
            query: LIST_CARS,
            variables: { limit: 200, nextToken },
            authMode: await smartAuthMode(),
          });

          const payload = carResult?.data?.listCars;
          nextToken = payload?.nextToken || null;
          allCars = allCars.concat((payload?.items || []).filter(Boolean));
        } while (nextToken);

        if (!cancelled) {
          setCompanies(allCompanies);
          setCars(allCars);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setDataErr(error?.message || 'Failed to load data.');
        }
      } finally {
        if (!cancelled) {
          setDataLoading(false);
        }
      }
    }

    loadAll();

    return () => {
      cancelled = true;
    };
  }, []);

  async function submitPartnerLead() {
    if (safeStr(agencyWebsite)) throw new Error('Bad request.');

    setSubmittingPartner(true);
    setPartnerDone(null);

    try {
      if (partnerSubmitLocked) throw new Error('Request already submitted.');
      if (!safeStr(partnerCaptchaToken)) throw new Error('Complete the anti-spam check.');

      if (!safeStr(agencyName)) throw new Error('Agency name is required.');
      if (!safeStr(agencyEmail)) throw new Error('Agency email is required.');
      if (!safeStr(agencyPhone)) throw new Error('Agency phone or WhatsApp is required.');
      if (!safeStr(agencyCountry)) throw new Error('Headquarters is required.');
      if (!safeStr(agencyLocationsCount)) throw new Error('Number of locations is required.');
      if (!safeStr(agencyMessage)) throw new Error('Message is required.');

      const gqlInput = {
        type: 'PARTNER_LEAD',
        name: agencyName,
        email: agencyEmail,
        phone: agencyPhone,
        whatsapp: agencyPhone,
        country: agencyCountry,
        message: agencyMessage,
        payload: JSON.stringify({
          fleetSize: safeStr(agencyFleetSize) || null,
          locationsCount: safeStr(agencyLocationsCount) || null,
          captcha: { provider: 'turnstile', token: partnerCaptchaToken, action: 'partner' },
          createdAtClient: new Date().toISOString(),
        }),
        source: 'landingpage',
        website: agencyWebsite,
      };

      const result = await client.graphql({
        query: SUBMIT_INBOUND,
        variables: { input: gqlInput },
        authMode: await smartAuthMode(),
      });

      const id = result?.data?.submitInboundRequest?.id;
      setPartnerDone({ ok: true, id });
      setPartnerSubmitLocked(true);
      setPartnerCaptchaToken('');
    } catch (error) {
      console.error(error);
      setPartnerDone({ ok: false, message: error?.message || 'Failed to submit franchising request.' });
    } finally {
      setSubmittingPartner(false);
    }
  }

  async function submitContactLead() {
    if (safeStr(contactWebsite)) throw new Error('Bad request.');

    setSubmittingContact(true);
    setContactDone(null);

    try {
      if (contactSubmitLocked) throw new Error('Request already submitted.');
      if (!safeStr(contactCaptchaToken)) throw new Error('Complete the anti-spam check.');

      if (!safeStr(contactName)) throw new Error('Name is required.');
      if (!safeStr(contactEmail)) throw new Error('Email is required.');
      if (!safeStr(contactMsg)) throw new Error('Message is required.');

      const gqlInput = {
        type: 'CONTACT_LEAD',
        name: contactName,
        email: contactEmail,
        phone: contactPhone,
        whatsapp: contactPhone,
        country: null,
        message: contactMsg,
        payload: JSON.stringify({
          reason: contactReason,
          createdAtClient: new Date().toISOString(),
          captcha: { provider: 'turnstile', token: contactCaptchaToken, action: 'contact' },
        }),
        source: 'landingpage',
        website: contactWebsite,
      };

      const result = await client.graphql({
        query: SUBMIT_INBOUND,
        variables: { input: gqlInput },
        authMode: await smartAuthMode(),
      });

      const id = result?.data?.submitInboundRequest?.id;
      setContactDone({ ok: true, id });
      setContactSubmitLocked(true);
      setContactCaptchaToken('');
    } catch (error) {
      console.error(error);
      setContactDone({ ok: false, message: error?.message || 'Failed to submit contact request.' });
    } finally {
      setSubmittingContact(false);
    }
  }

  const statsText = dataLoading
    ? 'Checking live availability...'
    : dataErr
      ? 'Live availability is temporarily unavailable.'
      : 'Live availability online';

  return (
    <div className="lp-root has-reveal-motion">
      <header className={`lp-header ${compactHeader ? 'is-compact' : ''}`}>
        <div className="lp-header-inner">
          <button
            type="button"
            className="lp-brand"
            onClick={() => window.scrollTo({ top: 0, behavior: getScrollBehavior() })}
            aria-label="Scroll to top"
          >
            <img src={logo} alt="Haste Car Rental" className="lp-brand-logo" />
          </button>

          <nav className="lp-nav" aria-label="Landing sections">
            <button
              type="button"
              className={`lp-tab ${active === 'reserve' ? 'is-active' : ''}`}
              aria-current={active === 'reserve' ? 'page' : undefined}
              onClick={() => goTo('reserve')}
            >
              Reserve
            </button>
            <button
              type="button"
              className={`lp-tab ${active === 'partner' ? 'is-active' : ''}`}
              aria-current={active === 'partner' ? 'page' : undefined}
              onClick={() => goTo('partner')}
            >
              Partners
            </button>
            <button
              type="button"
              className={`lp-tab ${active === 'contact' ? 'is-active' : ''}`}
              aria-current={active === 'contact' ? 'page' : undefined}
              onClick={() => goTo('contact')}
            >
              Support
            </button>

            <Link to="/console" className="lp-signin" title="Open console">
              Console
            </Link>
          </nav>

          <button type="button" className="lp-header-cta" onClick={() => goTo('reserve')}>
            Book <ArrowRight size={15} />
          </button>
        </div>
      </header>

      <main className="lp-main">
        <HeroSection
          dataLoading={dataLoading}
          dataErr={dataErr}
          onReserveClick={() => goTo('reserve')}
          onContactClick={() => goTo('contact')}
        />

        <section className="lp-booking-section" aria-labelledby="lp-booking-title">
          <div className="lp-booking-head" data-reveal="up">
            <div>
              <div className="lp-section-kicker">Reserve online</div>
              <h2 id="lp-booking-title">Your drive starts here.</h2>
            </div>
            <div className="lp-booking-status">
              <span className="lp-live-dot" />
              {statsText}
            </div>
          </div>

          <div className="lp-mode-tabs" role="tablist" aria-label="Choose a service" data-reveal="up">
            <button
              type="button"
              role="tab"
              aria-selected={active === 'reserve'}
              className={active === 'reserve' ? 'is-active' : ''}
              onClick={() => goTo('reserve')}
            >
              <CarFront size={16} /> Reserve
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={active === 'partner'}
              className={active === 'partner' ? 'is-active' : ''}
              onClick={() => goTo('partner')}
            >
              <Building2 size={16} /> Partner
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={active === 'contact'}
              className={active === 'contact' ? 'is-active' : ''}
              onClick={() => goTo('contact')}
            >
              <Mail size={16} /> Contact
            </button>
          </div>

          {dataErr ? (
            <Notice tone="error" title="Availability issue" compact>
              Live inventory is unavailable. Requests still work.
            </Notice>
          ) : null}

          <div className="lp-panel-shell" data-reveal="up">
            {active === 'reserve' && renderReserve()}
            {active === 'partner' && renderPartner()}
            {active === 'contact' && renderContact()}
          </div>
        </section>

        <FAQSection
          onContactClick={() => goTo('contact')}
          onPartnerClick={() => goTo('partner')}
        />
      </main>

      <footer className="lp-footer">
        <div className="lp-footer-main">
          <button
            type="button"
            className="lp-footer-brand"
            aria-label="Back to top"
            onClick={() => window.scrollTo({ top: 0, behavior: getScrollBehavior() })}
          >
            <img src={logo} alt="" className="lp-footer-logo" />
          </button>

          <div className="lp-footer-links">
            <a href={`https://wa.me/${WHATSAPP_E164}`} target="_blank" rel="noreferrer">WhatsApp</a>
            <a href={`mailto:${CONTACT_EMAIL}`}>Email</a>
            <Link to="/console">Console</Link>
          </div>
        </div>

        <div className="lp-footer-bottom">
          <span>Car rental, made simple</span>
          <span>© {year} Haste Car Rental</span>
        </div>
      </footer>
    </div>
  );

  function renderReserve() {
    return (
      <section ref={reservePanelRef} className="lp-panel lp-panel--flow">
        <ReservationFlow
          companies={reservationCompanies}
          cars={reservationCars}
          dataLoading={dataLoading}
          companyCountryOptions={reservationCountryOptions}
          turnstileSiteKey={TURNSTILE_SITE_KEY}
          isMobile={isMobile}
        />
      </section>
    );
  }

  function renderPartner() {
    return (
      <section ref={partnerPanelRef} className="lp-panel">
        <div className="lp-panel-title-row">
          <div className="lp-panel-icon">
            <Building2 size={18} />
          </div>
          <div>
            <h3>Grow with Haste.</h3>
            <p>Tell us about your rental business.</p>
          </div>
        </div>

        <div className="lp-info-box">
          <h4>Next</h4>
          <ul>
            <li>Review</li>
            <li>Onboarding</li>
            <li>Launch</li>
          </ul>
        </div>

        <div className="lp-honeypot" aria-hidden="true">
          <label>
            Website
            <input
              value={agencyWebsite}
              onChange={event => setAgencyWebsite(event.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </label>
        </div>

        <div id="franchise" ref={partnerFormRef} className="lp-form-grid lp-franchise-form">
          <label className="lp-field">
            <span>Agency name</span>
            <input
              className="lp-input"
              value={agencyName}
              onChange={event => setAgencyName(event.target.value)}
              placeholder="Company or brand"
            />
          </label>

          <label className="lp-field">
            <span>Headquarters</span>
            <input
              className="lp-input"
              value={agencyCountry}
              onChange={event => setAgencyCountry(event.target.value)}
              placeholder="Country"
            />
          </label>

          <label className="lp-field">
            <span>Number of locations</span>
            <input
              className="lp-input"
              value={agencyLocationsCount}
              onChange={event => setAgencyLocationsCount(event.target.value)}
              inputMode="numeric"
              placeholder="e.g. 3"
            />
          </label>

          <label className="lp-field">
            <span>Email</span>
            <input
              className="lp-input"
              value={agencyEmail}
              onChange={event => setAgencyEmail(event.target.value)}
              placeholder="agency@email.com"
            />
          </label>

          <label className="lp-field">
            <span>Phone or WhatsApp</span>
            <input
              className="lp-input"
              value={agencyPhone}
              onChange={event => setAgencyPhone(event.target.value)}
              placeholder="Country code + number"
            />
          </label>

          <label className="lp-field">
            <span>Fleet size (optional)</span>
            <input
              className="lp-input"
              value={agencyFleetSize}
              onChange={event => setAgencyFleetSize(event.target.value)}
              placeholder="e.g. 72"
            />
          </label>

          <label className="lp-field lp-field--full">
            <span>Message</span>
            <textarea
              className="lp-textarea"
              value={agencyMessage}
              onChange={event => setAgencyMessage(event.target.value)}
              placeholder="Share details about your coverage and operations"
            />
          </label>

          <div className="lp-field lp-field--full">
            <span>Anti-spam verification</span>
            <div className="lp-captcha-box">
              <TurnstileWidget
                siteKey={TURNSTILE_SITE_KEY}
                action="partner"
                onToken={token => setPartnerCaptchaToken(safeStr(token))}
                onExpire={() => setPartnerCaptchaToken('')}
                onError={() => setPartnerCaptchaToken('')}
              />
            </div>
          </div>
          <div className="lp-field lp-field--full">
            {!partnerSubmitLocked ? (
              <button
                type="button"
                className="lp-btn lp-btn--primary lp-btn--full"
                disabled={submittingPartner}
                onClick={submitPartnerLead}
              >
                {submittingPartner ? (
                  <>
                    <Loader2 size={16} className="spin" /> Sending...
                  </>
                ) : (
                  <>
                    <Mail size={16} /> Send franchising request
                  </>
                )}
              </button>
            ) : (
              <div className="lp-sent-state">Sent</div>
            )}
          </div>
        </div>

        <p className="lp-helper-text">We use this information only for onboarding communication.</p>

        {partnerDone?.ok ? (
          <Notice tone="success" title="Application sent" meta={`Reference: ${partnerDone.id}`} onClose={() => setPartnerDone(null)}>
            We received your request and will reach out with next steps.
          </Notice>
        ) : null}

        {partnerDone && !partnerDone.ok ? (
          <Notice tone="error" title="Unable to submit" onClose={() => setPartnerDone(null)}>
            {partnerDone.message}
          </Notice>
        ) : null}
      </section>
    );
  }

  function renderContact() {
    return (
      <section ref={contactPanelRef} className="lp-panel">
        <div className="lp-panel-title-row">
          <div className="lp-panel-icon">
            <Mail size={18} />
          </div>
          <div>
            <h3>Talk to a person.</h3>
            <p>We are here when plans change.</p>
          </div>
        </div>

        <div className="lp-inline-links">
          <div>
            <Globe2 size={15} /> Rental support
          </div>
          <div>
            <Phone size={15} />
            <a href={`https://wa.me/${WHATSAPP_E164}`} target="_blank" rel="noreferrer">
              {WHATSAPP_NUMBER}
            </a>
            <span className="lp-separator">|</span>
            <a href={`tel:${WHATSAPP_NUMBER}`}>Call</a>
          </div>
          <div>
            <Mail size={15} />
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
        </div>

        <div className="lp-honeypot" aria-hidden="true">
          <label>
            Website
            <input
              value={contactWebsite}
              onChange={event => setContactWebsite(event.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </label>
        </div>

        <div className="lp-form-grid">
          <label className="lp-field">
            <span>Your name</span>
            <input
              className="lp-input"
              value={contactName}
              onChange={event => setContactName(event.target.value)}
              placeholder="Full name"
            />
          </label>

          <label className="lp-field">
            <span>Your email</span>
            <input
              className="lp-input"
              value={contactEmail}
              onChange={event => setContactEmail(event.target.value)}
              placeholder="you@email.com"
            />
          </label>

          <label className="lp-field">
            <span>Phone or WhatsApp</span>
            <input
              className="lp-input"
              value={contactPhone}
              onChange={event => setContactPhone(event.target.value)}
              placeholder="Country code + number"
            />
          </label>

          <label className="lp-field">
            <span>Reason</span>
            <select className="lp-select" value={contactReason} onChange={event => setContactReason(event.target.value)}>
              <option value="GENERAL">General inquiry</option>
              <option value="PRICING">Pricing</option>
              <option value="SUPPORT">Support</option>
              <option value="COMPLAINT">Complaint</option>
              <option value="OTHER">Other</option>
            </select>
          </label>

          <label className="lp-field lp-field--full">
            <span>Message</span>
            <textarea
              className="lp-textarea"
              value={contactMsg}
              onChange={event => setContactMsg(event.target.value)}
              placeholder="Write your message"
            />
          </label>

          <div className="lp-field lp-field--full">
            <span>Anti-spam verification</span>
            <div className="lp-captcha-box">
              <TurnstileWidget
                siteKey={TURNSTILE_SITE_KEY}
                action="contact"
                onToken={token => setContactCaptchaToken(safeStr(token))}
                onExpire={() => setContactCaptchaToken('')}
                onError={() => setContactCaptchaToken('')}
              />
            </div>
          </div>

          <div className="lp-field lp-field--full">
            {!contactSubmitLocked ? (
              <button
                type="button"
                className="lp-btn lp-btn--primary lp-btn--full"
                disabled={submittingContact}
                onClick={submitContactLead}
              >
                {submittingContact ? (
                  <>
                    <Loader2 size={16} className="spin" /> Sending...
                  </>
                ) : (
                  <>
                    <Mail size={16} /> Send message
                  </>
                )}
              </button>
            ) : (
              <div className="lp-sent-state">Sent</div>
            )}
          </div>
        </div>

        {contactDone?.ok ? (
          <Notice tone="success" title="Message sent" meta={`Reference: ${contactDone.id}`} onClose={() => setContactDone(null)}>
            Thanks for reaching out. WhatsApp is best for urgent follow-up.
          </Notice>
        ) : null}

        {contactDone && !contactDone.ok ? (
          <Notice tone="error" title="Unable to submit" onClose={() => setContactDone(null)}>
            {contactDone.message}
          </Notice>
        ) : null}
      </section>
    );
  }
}
